import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '../lib/api';

export default function CustomerTicketDetailPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [reply, setReply] = useState('');
  const [csat, setCsat] = useState<number | null>(null);

  const { data: ticket } = useQuery({ queryKey: ['portal-ticket', id], queryFn: async () => (await api.get(`/portal/tickets/${id}`)).data });

  const send = useMutation({
    mutationFn: async () => api.post(`/portal/tickets/${id}/replies`, { body: reply }),
    onSuccess: () => { setReply(''); qc.invalidateQueries({ queryKey: ['portal-ticket', id] }); toast.success('Sent'); },
  });
  const submitCsat = useMutation({
    mutationFn: async (score: number) => api.post(`/portal/tickets/${id}/csat`, { score }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['portal-ticket', id] }); toast.success('Thanks for the feedback'); },
  });

  if (!ticket) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-8 max-w-3xl">
      <Link to="/portal" className="text-sm text-slate-500 hover:text-brand-600">← Back</Link>
      <div className="mt-2">
        <div className="text-xs font-mono text-slate-500">{ticket.ticketNo}</div>
        <h1 className="text-2xl font-semibold">{ticket.subject}</h1>
        <div className="mt-1 text-xs text-slate-500"><span className="badge bg-slate-100 mr-2">{ticket.status}</span><span className="badge bg-amber-100 text-amber-800">{ticket.priority}</span></div>
      </div>

      <div className="card p-5 mt-4">
        <div className="text-xs text-slate-500 uppercase mb-3">Conversation</div>
        <div className="space-y-3">
          {ticket.replies.map((r: { id: string; body: string; createdAt: string; agent?: { name: string } | null; endCustomer?: { name: string } | null }) => (
            <div key={r.id} className={'rounded-lg p-3 ' + (r.agent ? 'bg-brand-50 border border-brand-100' : 'bg-slate-50 border border-slate-200')}>
              <div className="text-xs text-slate-500 mb-1"><strong>{r.agent?.name ?? r.endCustomer?.name ?? 'You'}</strong> · {new Date(r.createdAt).toLocaleString()}</div>
              <div className="whitespace-pre-wrap text-sm text-slate-800">{r.body}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="card p-5 mt-4">
        <textarea className="input min-h-[100px]" placeholder="Reply…" value={reply} onChange={(e) => setReply(e.target.value)} />
        <div className="mt-3 text-right">
          <button className="btn-primary" disabled={!reply || send.isPending} onClick={() => send.mutate()}>{send.isPending ? 'Sending…' : 'Send reply'}</button>
        </div>
      </div>

      {ticket.status === 'RESOLVED' && !ticket.csatScore && (
        <div className="card p-5 mt-4">
          <div className="text-sm font-medium mb-2">How was your experience?</div>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} className={'btn-ghost ' + (csat === n ? 'bg-brand-100 text-brand-800' : '')} onMouseEnter={() => setCsat(n)} onClick={() => submitCsat.mutate(n)}>{'★'.repeat(n)}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
